import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { CalendarIcon, Clock, UserRound, Home, MapPin } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";

const EXTRAS_ICONS = {
  lavandaria: "L", consumiveis: "C", limpeza_profunda: "P", churrasqueira: "G", relva: "R", manutencao: "M",
};

const STANDARD_TYPES = new Set(["T0", "T1", "T2", "T3", "T4", "T5", "VS", "VM", "VL", "HOTEL"]);
const WALK_IN_TYPES = new Set(["DEEP_CLEAN", "POST_OBRA", "MUDANCA", "AR_CONDICIONADO", "ESTOFOS", "VIDROS", "AVULSO"]);

const TIME_SLOTS = (() => {
  const arr = [];
  for (let h = 8; h <= 20; h++) {
    for (const m of [0, 30]) arr.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
  return arr;
})();

/**
 * AdminNewBooking — lets the admin create a cleaning booking ON BEHALF of any client.
 * Flow: pick client → apartment (auto-filtered by client) → type/date/time → extras → submit.
 * Uses the same `POST /bookings` endpoint with the admin-only `client_id` field.
 */
export default function AdminNewBooking() {
  const nav = useNavigate();

  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [types, setTypes] = useState([]);
  const [extras, setExtras] = useState([]);

  const [form, setForm] = useState({
    client_id: "",
    apt_type: "T0",
    apartment_id: null,
    scheduled_date: "",
    scheduled_time: "",
    notes: "",
    extras: [],
    assigned_employee_id: "",
    auto_assign: false,
    guest_name: "",
    guest_phone: "",
    entry_address: "",
    entry_instructions: "",
    discount: 0,            // €, applied to the booking total
    discount_reason: "",    // free-text shown on the invoice line
    // Walk-in (limpeza avulso) — sem cliente, com preço manual
    is_walk_in: false,
    manual_price: "",
    walk_in_description: "",
  });
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Load static catalogs + clients + employees + apartments
  useEffect(() => {
    api.get("/catalog/apartment-types").then(({ data }) => setTypes(data.items));
    api.get("/catalog/extras").then(({ data }) => setExtras(data.items));
    api.get("/admin/users", { params: { role: "client" } }).then(({ data }) => setClients(data.items));
    api.get("/admin/users", { params: { role: "employee" } }).then(({ data }) =>
      setEmployees(data.items.filter(e => e.approved)));
    api.get("/apartments").then(({ data }) => setApartments(data.items)).catch(() => {});
  }, []);

  // Apartments filtered by the selected client (admin fetches all owners)
  const clientApartments = useMemo(() => {
    if (!form.client_id) return [];
    return apartments.filter(a => a.owner_id === form.client_id);
  }, [apartments, form.client_id]);

  const selectedClient = useMemo(
    () => clients.find(c => c.id === form.client_id),
    [clients, form.client_id]
  );

  const canPreview = !form.is_walk_in && form.scheduled_date && form.scheduled_time;

  useEffect(() => {
    if (!canPreview) { setPreview(null); return; }
    let cancelled = false;
    api.post("/catalog/price-preview", {
      apt_type: form.apt_type,
      apartment_id: form.apartment_id,
      scheduled_date: form.scheduled_date,
      scheduled_time: form.scheduled_time,
      extras: form.extras,
    })
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.apt_type, form.apartment_id, form.scheduled_date, form.scheduled_time, JSON.stringify(form.extras), canPreview]); // eslint-disable-line

  function toggleExtra(id) {
    setForm(f => ({ ...f, extras: f.extras.includes(id) ? f.extras.filter(x => x !== id) : [...f.extras, id] }));
  }

  function pickDate(d) {
    if (!d) return;
    setSelectedDate(d);
    setForm(f => ({ ...f, scheduled_date: format(d, "yyyy-MM-dd") }));
    setCalendarOpen(false);
  }

  function onPickClient(cid) {
    const c = clients.find(x => x.id === cid);
    setForm(f => ({
      ...f,
      client_id: cid,
      apartment_id: null,
      // Pre-fill guest fields with client data as a sensible default
      guest_name: f.guest_name || c?.name || "",
      guest_phone: f.guest_phone || c?.phone || "",
    }));
  }

  function onPickApartment(aid) {
    const a = apartments.find(x => x.id === aid);
    setForm(f => ({
      ...f,
      apartment_id: aid,
      apt_type: a?.apt_type || f.apt_type,
      entry_address: f.entry_address || a?.address || "",
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.is_walk_in && !form.client_id) { toast.error("Escolha um cliente (ou ative «Limpeza avulso»)."); return; }
    if (form.is_walk_in) {
      const price = parseFloat(form.manual_price);
      if (!form.manual_price || isNaN(price) || price <= 0) {
        toast.error("Defina o preço da limpeza avulso.");
        return;
      }
      if (!form.walk_in_description || form.walk_in_description.trim().length < 5) {
        toast.error("Descreva brevemente a limpeza avulso (mínimo 5 caracteres).");
        return;
      }
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.assigned_employee_id) delete payload.assigned_employee_id;
      // Walk-in cleanings ditch client_id and use manual_price
      if (form.is_walk_in) {
        delete payload.client_id;
        delete payload.apartment_id;
        payload.manual_price = parseFloat(form.manual_price);
      } else {
        delete payload.manual_price;
        delete payload.walk_in_description;
      }
      delete payload.is_walk_in;
      // Clean optional discount
      const disc = Number(payload.discount || 0);
      if (!disc || disc <= 0) {
        delete payload.discount; delete payload.discount_reason;
      } else {
        payload.discount = Math.round(disc * 100) / 100;
      }
      await api.post("/bookings", payload);
      toast.success(form.is_walk_in ? "Limpeza avulso criada." : "Reserva criada para o cliente.");
      nav("/admin/reservas");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.reason === "missing_required_fields") {
        const labels = {
          guest_name: "Nome da pessoa",
          guest_phone: "Telemóvel",
          entry_address: "Endereço",
          entry_instructions: "Instruções para entrada",
          walk_in_description: "Descrição da limpeza avulso",
        };
        const items = (detail.missing || []).map(k => labels[k] || k).join(", ");
        toast.error(`Preencha: ${items}`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setBusy(false); }
  }

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  return (
    <div className="max-w-6xl space-y-10">
      <div>
        <div className="eyebrow mb-3">Admin · Nova reserva</div>
        <h1 className="font-display text-5xl">Agendar limpeza em nome de um cliente.</h1>
        <div className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Centralize aqui todas as reservas — o cliente é notificado e pode acompanhar o estado na sua área.
        </div>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8" data-testid="admin-new-booking-form">
        <div className="lg:col-span-2 space-y-6">
          {/* TOGGLE — Limpeza avulso */}
          <div className="lux-card p-5 border-[#d4af37]/30">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_walk_in}
                onChange={e => setForm(f => ({
                  ...f,
                  is_walk_in: e.target.checked,
                  client_id: e.target.checked ? "" : f.client_id,
                  apartment_id: e.target.checked ? null : f.apartment_id,
                  apt_type: e.target.checked ? "DEEP_CLEAN" : (f.apt_type.length === 2 ? f.apt_type : "T0"),
                }))}
                className="w-4 h-4 accent-[#d4af37]"
                data-testid="admin-toggle-walk-in"
              />
              <div className="flex-1">
                <div className="text-[#fdfbf7] text-sm font-semibold">Limpeza avulso (sem cliente)</div>
                <div className="text-xs text-[#a0b2af] mt-0.5">
                  Para deep clean, pós-obra, mudanças, etc. Atribui funcionário e faz relatório
                  normalmente. Pede-se os dados do cliente apenas quando emitir fatura.
                </div>
              </div>
            </label>
          </div>

          {form.is_walk_in && (
            <div className="lux-card p-6 space-y-4">
              <div className="eyebrow text-[#d4af37]">Detalhes da limpeza avulso</div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Descrição</label>
                <input
                  value={form.walk_in_description}
                  onChange={e => setForm(f => ({ ...f, walk_in_description: e.target.value }))}
                  placeholder="Ex: Limpeza profunda T3 após hóspede problemático — Funchal"
                  className="mt-1 w-full bg-[#062c27] border border-[#1a4640] focus:border-[#d4af37] outline-none px-4 py-2.5 text-sm text-[#fdfbf7] rounded-sm"
                  data-testid="admin-walk-in-description"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Preço (€)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={form.manual_price}
                  onChange={e => setForm(f => ({ ...f, manual_price: e.target.value }))}
                  placeholder="120.00"
                  className="mt-1 w-full bg-[#062c27] border border-[#1a4640] focus:border-[#d4af37] outline-none px-4 py-2.5 text-sm text-[#fdfbf7] rounded-sm"
                  data-testid="admin-walk-in-price"
                />
              </div>
            </div>
          )}

          {/* STEP 1 — CLIENT */}
          {!form.is_walk_in && (
          <div className="lux-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <UserRound size={16} className="text-[#d4af37]" />
              <div className="eyebrow">1 · Cliente (Host)</div>
            </div>
            <select required value={form.client_id} onChange={e => onPickClient(e.target.value)}
                    className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-sm focus:border-[#d4af37] outline-none"
                    data-testid="admin-booking-client">
              <option value="">— Escolher cliente —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.email}{c.registration_number ? ` · ${c.registration_number}` : ""}
                </option>
              ))}
            </select>
            {selectedClient && (
              <div className="mt-3 text-xs text-[#a0b2af] flex flex-wrap gap-4" data-testid="selected-client-info">
                {selectedClient.phone && <span>Tel: <span className="text-[#d4af37]">{selectedClient.phone}</span></span>}
                <span>Email: <span className="text-[#d4af37]">{selectedClient.email}</span></span>
                {selectedClient.registration_number && (
                  <span>Nº Cliente: <span className="text-[#d4af37] font-mono">{selectedClient.registration_number}</span></span>
                )}
              </div>
            )}
          </div>
          )}

          {/* STEP 2 — APARTMENT */}
          {!form.is_walk_in && form.client_id && (
            <div className="lux-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Home size={16} className="text-[#d4af37]" />
                <div className="eyebrow">2 · Apartamento</div>
              </div>
              {clientApartments.length === 0 ? (
                <div className="text-sm text-[#a0b2af]">
                  Este cliente ainda não tem apartamentos registados. Escolha a tipologia manualmente em baixo.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setForm(f => ({ ...f, apartment_id: null }))}
                          className={`px-3 py-2 text-sm border rounded-sm ${!form.apartment_id ? "border-[#d4af37] text-[#d4af37]" : "border-[#1a4640] text-[#a0b2af]"}`}
                          data-testid="admin-apt-none">Sem apartamento</button>
                  {clientApartments.map(a => (
                    <button type="button" key={a.id} onClick={() => onPickApartment(a.id)}
                            className={`px-3 py-2 text-sm border rounded-sm ${form.apartment_id === a.id ? "border-[#d4af37] text-[#d4af37]" : "border-[#1a4640] text-[#a0b2af]"}`}
                            data-testid={`admin-apt-${a.id}`}>
                      {a.name} · {a.apt_type}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — TYPE */}
          <div className="lux-card p-6">
            <div className="eyebrow mb-4">3 · {form.is_walk_in ? "Tipo de serviço avulso" : "Tipologia"}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {types
                .filter(t => form.is_walk_in ? WALK_IN_TYPES.has(t.type) : STANDARD_TYPES.has(t.type))
                .map(t => (
                <button type="button" key={t.type} onClick={() => setForm(f => ({ ...f, apt_type: t.type }))}
                        className={`p-4 border rounded-sm text-left transition-colors ${form.apt_type === t.type ? "border-[#d4af37] bg-[#0a3a33]" : "border-[#1a4640] hover:border-[#d4af37]/60"}`}
                        data-testid={`admin-type-${t.type}`}>
                  <div className="font-display text-2xl">{form.is_walk_in ? (t.label || t.type).split(" ")[0] : t.type}</div>
                  <div className="text-[11px] text-[#a0b2af] mt-0.5 truncate">{t.label || t.type}</div>
                  <div className="text-xs text-[#a0b2af] mt-2">
                    {Math.floor(t.duration / 60)}h{t.duration % 60 ? ` ${t.duration % 60}m` : ""}
                    <span className="text-[#d4af37]/60"> · +{t.buffer_minutes}m buffer</span>
                  </div>
                  {!form.is_walk_in && (
                    <div className="text-sm text-[#d4af37] mt-1">{t.current_price}€</div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* STEP 4 — DATE/TIME */}
          <div className="lux-card p-6 grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Data</label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button type="button"
                    className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-left flex items-center justify-between hover:border-[#d4af37]"
                    data-testid="admin-booking-date">
                    <span className={selectedDate ? "" : "text-[#a0b2af]"}>
                      {selectedDate ? format(selectedDate, "EEEE, d 'de' MMMM yyyy", { locale: pt }) : "Escolher data"}
                    </span>
                    <CalendarIcon size={16} className="text-[#d4af37]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 bg-[#062c27] border-[#1a4640]" align="start">
                  <Calendar mode="single" selected={selectedDate} onSelect={pickDate}
                            disabled={{ before: today }} locale={pt} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Hora</label>
              <div className="relative">
                <select required value={form.scheduled_time}
                        onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))}
                        className="w-full appearance-none bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 pr-10 outline-none focus:border-[#d4af37]"
                        data-testid="admin-booking-time">
                  <option value="">Escolher hora</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <Clock size={16} className="text-[#d4af37] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* STEP 5 — MANDATORY ACCESS FIELDS */}
          <div className="lux-card p-6 space-y-4" data-testid="admin-mandatory-fields">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-[#d4af37]" />
              <div>
                <div className="eyebrow">5 · Dados do hóspede & acesso</div>
                <div className="text-[11px] text-[#a0b2af] mt-1">Obrigatórios · a equipa de limpeza precisa destas informações.</div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Nome da pessoa *</label>
                <input type="text" required minLength={2} value={form.guest_name}
                       onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                       className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                       data-testid="admin-guest-name" />
              </div>
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Telemóvel *</label>
                <input type="tel" required minLength={6} value={form.guest_phone}
                       onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value }))}
                       placeholder="+351 9XX XXX XXX"
                       className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                       data-testid="admin-guest-phone" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Endereço completo *</label>
                <input type="text" required minLength={5} value={form.entry_address}
                       onChange={e => setForm(f => ({ ...f, entry_address: e.target.value }))}
                       placeholder="Rua · nº · CP · localidade"
                       className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                       data-testid="admin-entry-address" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Instruções para entrada *</label>
                <textarea required minLength={3} rows={3} value={form.entry_instructions}
                          onChange={e => setForm(f => ({ ...f, entry_instructions: e.target.value }))}
                          placeholder="Ex: Código da porta, localização das chaves, contacto no local…"
                          className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                          data-testid="admin-entry-instructions" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Notas (opcional)</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                          rows={2} className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                          data-testid="admin-booking-notes" />
              </div>
            </div>
          </div>

          {/* STEP 6 — EXTRAS */}
          <div className="lux-card p-6">
            <div className="eyebrow mb-4">6 · Extras</div>
            <div className="grid md:grid-cols-2 gap-3">
              {extras.map(ex => (
                <button type="button" key={ex.id} onClick={() => toggleExtra(ex.id)}
                        className={`p-4 border flex items-center justify-between ${form.extras.includes(ex.id) ? "border-[#d4af37] bg-[#0a3a33]" : "border-[#1a4640]"}`}
                        data-testid={`admin-extra-${ex.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center text-xs font-mono border border-[#d4af37]/50 text-[#d4af37]">
                      {EXTRAS_ICONS[ex.id]}
                    </span>
                    <span className="text-sm">{ex.name}</span>
                  </div>
                  <span className="text-[#d4af37] text-sm whitespace-nowrap">
                    {ex.price_on_request ? "A combinar" : `+${ex.price}€`}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* STEP 7 — EMPLOYEE */}
          <div className="lux-card p-6 space-y-3">
            <div className="eyebrow">7 · Colaborador</div>
            <div className="flex flex-wrap gap-3 items-center">
              <select value={form.assigned_employee_id}
                      onChange={e => setForm(f => ({ ...f, assigned_employee_id: e.target.value, auto_assign: false }))}
                      className="bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-2 text-sm"
                      data-testid="admin-assign-employee">
                <option value="">— Atribuir manualmente —</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-[#a0b2af] cursor-pointer">
                <input type="checkbox" checked={form.auto_assign}
                       onChange={e => setForm(f => ({ ...f, auto_assign: e.target.checked, assigned_employee_id: "" }))}
                       className="accent-[#d4af37]"
                       data-testid="admin-auto-assign" />
                Atribuir automaticamente ao primeiro colaborador livre
              </label>
            </div>
          </div>
        </div>

        {/* SUMMARY */}
        <aside className="lux-card p-6 h-fit sticky top-6" data-testid="admin-booking-summary">
          <div className="eyebrow mb-4">Resumo</div>
          {form.is_walk_in ? (
            <div className="space-y-3 text-sm">
              <div className="pb-3 border-b border-[#1a4640]">
                <div className="eyebrow mb-1 text-[#d4af37]">Limpeza avulso</div>
                <div className="text-[#fdfbf7] text-xs mt-1">
                  Sem cliente registado. Dados fiscais só pedidos ao emitir fatura.
                </div>
              </div>
              {form.walk_in_description && (
                <div className="text-xs text-[#a0b2af]">
                  <span className="text-[#fdfbf7]">{form.walk_in_description}</span>
                </div>
              )}
              <div className="border-t border-[#1a4640] pt-3 flex items-center justify-between">
                <div className="eyebrow">Total</div>
                <div className="font-display text-3xl text-[#d4af37]">
                  {form.manual_price ? `${parseFloat(form.manual_price).toFixed(2)}€` : "—"}
                </div>
              </div>
            </div>
          ) : (
            <>
              {!form.client_id && <div className="text-sm text-[#a0b2af]">Comece por escolher um cliente.</div>}
              {form.client_id && !canPreview && <div className="text-sm text-[#a0b2af]">Escolha data e hora para calcular o preço.</div>}
            </>
          )}
          {preview && !form.is_walk_in && (
            <div className="space-y-3 text-sm">
              {selectedClient && (
                <div className="pb-3 border-b border-[#1a4640]">
                  <div className="eyebrow mb-1">Cliente</div>
                  <div className="text-[#fdfbf7]">{selectedClient.name}</div>
                  <div className="text-xs text-[#a0b2af]">{selectedClient.email}</div>
                </div>
              )}
              <Row k={`Base ${form.apt_type}`} v={`${preview.base.toFixed(2)}€`} />
              {preview.extras_detail?.map(e => <Row key={e.id} k={e.name} v={`+${e.price.toFixed(2)}€`} />)}
              {Number(form.discount) > 0 && (
                <Row k={form.discount_reason ? `Desconto · ${form.discount_reason}` : "Desconto"}
                     v={`−${Number(form.discount).toFixed(2)}€`} />
              )}
              <div className="border-t border-[#1a4640] pt-3 flex items-center justify-between">
                <div className="eyebrow">Total</div>
                <div className="font-display text-3xl text-[#d4af37]">
                  {Math.max(0, preview.total - Number(form.discount || 0)).toFixed(2)}€
                </div>
              </div>
              {preview.promo_active && <div className="text-xs text-[#10b981]">Preço promocional aplicado.</div>}

              {/* Discount block — admin-only override */}
              <div className="mt-3 pt-3 border-t border-[#1a4640] space-y-2"
                   data-testid="admin-booking-discount-block">
                <div className="eyebrow text-[#d4af37]">Promoção / desconto (opcional)</div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Valor (€)</span>
                    <input type="number" min="0" step="0.01"
                           value={form.discount}
                           onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                           className="w-full bg-[#062c27] border border-[#1a4640] px-2 py-1.5 text-[#fdfbf7] rounded-sm focus:border-[#d4af37] outline-none text-sm tabular-nums"
                           placeholder="0,00"
                           data-testid="admin-booking-discount-input" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Motivo</span>
                    <input type="text"
                           value={form.discount_reason}
                           onChange={e => setForm(f => ({ ...f, discount_reason: e.target.value }))}
                           className="w-full bg-[#062c27] border border-[#1a4640] px-2 py-1.5 text-[#fdfbf7] rounded-sm focus:border-[#d4af37] outline-none text-sm"
                           placeholder="ex.: cliente fidelidade"
                           data-testid="admin-booking-discount-reason" />
                  </label>
                </div>
              </div>

              {preview.workforce && preview.workforce.total_employees > 0 && (
                <div className="mt-3 flex items-center justify-between text-xs border-t border-[#1a4640] pt-3" data-testid="admin-capacity-info">
                  <span className="text-[#a0b2af]">Equipa disponível</span>
                  <span className={`font-mono ${preview.workforce.available_count > 0 ? "text-[#10b981]" : "text-[#fca5a5]"}`}>
                    {preview.workforce.available_count} / {preview.workforce.total_employees}
                  </span>
                </div>
              )}

              {!preview.available && (
                <div className="mt-4 p-4 border border-[#ef4444]/40 bg-[#ef4444]/10 rounded-sm space-y-2">
                  <div className="text-sm text-[#fca5a5]">Horário indisponível.</div>
                  {preview.alternatives?.length > 0 && (
                    <div>
                      <div className="text-xs text-[#a0b2af] mb-2">Próximos horários livres:</div>
                      <div className="flex flex-wrap gap-2">
                        {preview.alternatives.map(a => (
                          <button type="button" key={a} onClick={() => setForm(f => ({ ...f, scheduled_time: a }))}
                                  className="px-3 py-1 border border-[#d4af37] text-[#d4af37] text-xs rounded-sm hover:bg-[#d4af37]/10">{a}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <button disabled={busy || (form.is_walk_in
                    ? (!form.scheduled_date || !form.scheduled_time || !form.manual_price || !form.walk_in_description)
                    : (!form.client_id || !preview?.available))}
                  className="btn-gold w-full py-3 mt-6 disabled:opacity-50" data-testid="admin-booking-submit">
            {busy ? "A criar…" : (form.is_walk_in ? "Criar limpeza avulso" : "Criar reserva para cliente")}
          </button>
          {/* Why is the button disabled? Tell the admin */}
          {!busy && (
            <div className="mt-2 text-[11px] text-[#a0b2af] text-center min-h-[16px]"
                 data-testid="admin-booking-submit-help">
              {form.is_walk_in
                ? (!form.walk_in_description
                    ? "Adicione uma descrição da limpeza."
                    : !form.manual_price
                      ? "Defina o preço da limpeza avulso."
                      : !canPreview && (!form.scheduled_date || !form.scheduled_time)
                        ? "Escolha data e hora."
                        : null)
                : (!form.client_id
                  ? "Selecione um cliente para continuar."
                  : !canPreview
                    ? "Escolha data e hora para calcular o preço."
                    : !preview
                      ? "A calcular preço…"
                      : !preview.available
                        ? (preview.reason
                            ? `Indisponível: ${preview.reason}`
                            : "Horário indisponível — escolha outra hora.")
                        : null)}
            </div>
          )}
        </aside>
      </form>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-[#a0b2af]">{k}</div>
      <div>{v}</div>
    </div>
  );
}
